import React, { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import type { UniversalLogInput } from '../types/logging';
import { useUniversalLogger } from '../hooks/useUniversalLogger';
import { isDevEnvironment } from '../lib/env';

type InnerErrorBoundaryProps = {
  onError: (error: Error, info: React.ErrorInfo) => void;
  fallback?: ReactNode;
  children: ReactNode;
  resetKey?: string;
};

type InnerErrorBoundaryState = {
  hasError: boolean;
};

class InnerErrorBoundary extends React.Component<InnerErrorBoundaryProps, InnerErrorBoundaryState> {
  constructor(props: InnerErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): InnerErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.props.onError(error, info);
  }

  componentDidUpdate(prevProps: InnerErrorBoundaryProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}

export type UniversalErrorBoundaryProps = {
  children: ReactNode;
  fallback?: ReactNode;
};

export function UniversalErrorBoundary({ children, fallback }: UniversalErrorBoundaryProps) {
  const { logError } = useUniversalLogger();
  const location = useLocation();

  const resetKey = useMemo(() => location.pathname + location.search + location.hash, [location]);

  const handleError = useCallback(
    (error: Error, info: React.ErrorInfo) => {
      const payload: UniversalLogInput = {
        level: 'error',
        message: error?.message || 'Unhandled React error boundary exception',
        category: 'react/error-boundary',
        source: 'frontend',
        page: resetKey,
        details: {
          name: error?.name,
          stack: error?.stack,
          componentStack: info?.componentStack,
        },
      };
      logError(payload.message, payload);
      if (isDevEnvironment()) {
        // eslint-disable-next-line no-console
        console.error('UniversalErrorBoundary caught an error', error, info);
      }
    },
    [logError, resetKey]
  );

  return (
    <InnerErrorBoundary onError={handleError} fallback={fallback} resetKey={resetKey}>
      {children}
    </InnerErrorBoundary>
  );
}
